import {
  analyticsApi,
  integrationApi,
  projectApi,
  type Project,
} from '@shared/api-client';
import { selectedTrackingGoals } from '@shared/util/tracking-plan';
import { useQueries, useQuery } from '@tanstack/react-query';
import { signalObservation } from '@/lib/setup-status';
import { revenueProvidersForSetup } from '@/lib/onboarding-integrations';

export const useSetupSignals = (project: Project, enabled = true) => {
  const plan = project.profile?.trackingPlan;
  const active = enabled && Boolean(plan);
  const mobile = project.profile?.platform === 'mobile';
  const selected = selectedTrackingGoals(plan);
  const definitions = useQuery({
    queryKey: ['project-goals', project.id],
    queryFn: () => projectApi.listGoals(project.id),
    enabled: active,
    staleTime: 30000,
  });
  const realtime = useQuery({
    queryKey: ['analytics', 'project', project.id, 'realtime'],
    queryFn: () => analyticsApi.getRealtime(project.id),
    enabled: active && !mobile,
    refetchInterval: active ? 10000 : false,
    retry: false,
  });
  const checks = useQueries({
    queries: selected.map((event) => {
      const goal = definitions.data?.goals.find(
        (goal) => goal.kind === 'event' && goal.eventName === event.eventName,
      );
      return {
        queryKey: [
          'setup-goal-status',
          project.id,
          goal?.id ?? event.eventName,
        ],
        queryFn: () =>
          analyticsApi.getGoalAnalytics(project.id, goal!.id, '7d'),
        enabled: active && Boolean(goal),
        refetchInterval: active ? 10000 : false,
        staleTime: 5000,
        retry: false,
      };
    }),
  });
  const providers = revenueProvidersForSetup(
    project.profile?.platform ?? 'web',
    plan?.revenue ?? 'none',
  );
  const integrations = useQuery({
    queryKey: ['integrations', project.id],
    queryFn: () => integrationApi.listIntegrations(project.id),
    enabled: active && providers.length > 0,
    refetchInterval: active ? 30000 : false,
  });
  const websiteReceived =
    !mobile &&
    !realtime.isError &&
    (realtime.data?.summary.pageviews30m ?? 0) > 0;
  const goals = selected.map((event, index) => {
    const definition = definitions.data?.goals.find(
      (goal) => goal.kind === 'event' && goal.eventName === event.eventName,
    );
    const query = checks[index];
    return {
      ...event,
      prepared: Boolean(definition),
      observation: signalObservation(
        query?.data?.summary.lastConvertedAt,
        project.profile?.setupPreparedAt,
      ),
      lastObservedAt: query?.data?.summary.lastConvertedAt,
      loading:
        definitions.isPending || (Boolean(definition) && query?.isPending),
      error: definitions.isError || query?.isError,
      retry: () => (definition ? query?.refetch() : definitions.refetch()),
    };
  });
  const connectedProviders = providers.filter((provider) =>
    integrations.data?.integrations.some(
      (item) =>
        item.provider === provider &&
        ['connected', 'backfilling', 'live'].includes(item.status),
    ),
  );
  const revenueConnected =
    !integrations.isError &&
    providers.length > 0 &&
    (plan?.revenue === 'both'
      ? connectedProviders.length === providers.length
      : connectedProviders.length > 0);
  const signalsReceived =
    (mobile || websiteReceived) &&
    goals.every(
      (goal) => !goal.error && goal.prepared && goal.observation === 'received',
    );
  return {
    mobile,
    websiteReceived,
    realtime,
    goals,
    providers,
    revenueConnected,
    integrations,
    signalsReceived: signalsReceived && (!mobile || goals.length > 0),
  };
};
